import { useEffect, useState } from 'react';
import { unlockedSet } from './achievements';
import { getRunHistory } from './runHistory';

type SectionKey = 'stats' | 'codex';

const LS_CODEX_SEEN_KEY = 'cradle7.seenCodex'; // JSON array of seen entry IDs

/** Returns the set of codex entry IDs the player has SEEN (tapped/expanded or visible-while-unlocked). */
function getSeenCodexIds(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(LS_CODEX_SEEN_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed) : new Set();
  } catch { return new Set(); }
}

function saveSeenCodexIds(set: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_CODEX_SEEN_KEY, JSON.stringify([...set]));
  } catch { /* ignore */ }
}

/** Mark a codex entry as seen. Call when player taps to expand it. */
export function markCodexEntrySeen(id: string): void {
  const seen = getSeenCodexIds();
  if (seen.has(id)) return;
  seen.add(id);
  saveSeenCodexIds(seen);
  notify();
}

/** Mark ALL currently-unlocked codex entries as seen — call when the codex tab is opened. */
export function markCodexTabViewed(): void {
  const seen = getSeenCodexIds();
  const history = getRunHistory();
  const totalKills = history.reduce((s, r) => s + r.kills, 0);
  const wonAny30 = history.some((r) => r.outcome === 'won' && r.kills >= 20);

  const unlockedEntries: string[] = ['colony', 'bastet', 'earth_ai', 'nine_lives', 'med_log'];
  if (totalKills >= 1) unlockedEntries.push('enemy_grunt');
  if (totalKills >= 5) unlockedEntries.push('enemy_fast');
  if (totalKills >= 15) unlockedEntries.push('enemy_tank');
  if (wonAny30) unlockedEntries.push('enemy_boss');
  if (history.length >= 3) unlockedEntries.push('station_map_alpha');
  if (history.some((r) => r.outcome === 'won')) unlockedEntries.push('ai_dialogue_fragment');
  if (totalKills >= 50) unlockedEntries.push('nine_lives_origin');

  let changed = false;
  for (const id of unlockedEntries) {
    if (!seen.has(id)) {
      seen.add(id);
      changed = true;
    }
  }
  if (changed) {
    saveSeenCodexIds(seen);
    notify();
  }
}

/** Snapshot of achievements seen so far — set when the stats tab is opened. */
function getSeenAchievementIds(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem('cradle7.seenAchievements');
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed) : new Set();
  } catch { return new Set(); }
}

function saveSeenAchievementIds(set: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem('cradle7.seenAchievements', JSON.stringify([...set]));
  } catch { /* ignore */ }
}

/** Mark all currently-unlocked achievements as seen. Call when stats tab is opened. */
export function markStatsTabViewed(): void {
  const unlocked = unlockedSet(getRunHistory());
  const seen = getSeenAchievementIds();
  let changed = false;
  for (const id of unlocked) {
    if (!seen.has(id)) {
      seen.add(id);
      changed = true;
    }
  }
  if (changed) {
    saveSeenAchievementIds(seen);
    notify();
  }
}

/** Compute badge count for a section. Returns 0 if no new items. */
export function getBadgeCount(section: SectionKey): number {
  if (section === 'stats') {
    const unlocked = unlockedSet(getRunHistory());
    const seen = getSeenAchievementIds();
    let count = 0;
    for (const id of unlocked) {
      if (!seen.has(id)) count++;
    }
    return count;
  }
  if (section === 'codex') {
    const history = getRunHistory();
    const totalKills = history.reduce((s, r) => s + r.kills, 0);
    const wonAny30 = history.some((r) => r.outcome === 'won' && r.kills >= 20);
    const unlockedEntries: string[] = ['colony', 'bastet', 'earth_ai', 'nine_lives', 'med_log'];
    if (totalKills >= 1) unlockedEntries.push('enemy_grunt');
    if (totalKills >= 5) unlockedEntries.push('enemy_fast');
    if (totalKills >= 15) unlockedEntries.push('enemy_tank');
    if (wonAny30) unlockedEntries.push('enemy_boss');
    if (history.length >= 3) unlockedEntries.push('station_map_alpha');
    if (history.some((r) => r.outcome === 'won')) unlockedEntries.push('ai_dialogue_fragment');
    if (totalKills >= 50) unlockedEntries.push('nine_lives_origin');
    const seen = getSeenCodexIds();
    return unlockedEntries.filter((id) => !seen.has(id)).length;
  }
  return 0;
}

// ----- pub-sub for reactivity -----
const listeners = new Set<() => void>();
function notify(): void { listeners.forEach((fn) => fn()); }

/** React hook returning badge count for a section. Re-renders when badges change. */
export function useBadgeCount(section: SectionKey): number {
  const [count, setCount] = useState(() => getBadgeCount(section));
  useEffect(() => {
    const fn = () => setCount(getBadgeCount(section));
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, [section]);
  return count;
}

/** Manually trigger badge re-computation (e.g. after a run-end save). */
export function refreshBadges(): void {
  notify();
}
